import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { V2Spinner } from "./V2BusyFrame";
import { shiftIsoDate, toIsoDate } from "./v2OverviewMapper";

const FILTER_BTN =
  "inline-flex min-h-[34px] items-center gap-1.5 rounded-[5px] border border-[rgba(98,94,166,0.62)] bg-[rgba(17,12,61,0.52)] px-3 text-[12px] text-[#e5e2ff] hover:border-v2-cyan hover:bg-v2-accent";

const SHORTCUT =
  "flex min-h-8 w-full items-center rounded-[5px] border-0 px-2.5 text-left text-[12px] text-[#f1efff] disabled:cursor-not-allowed disabled:opacity-35";

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function monthStart(iso) {
  const date = new Date(`${iso}T00:00:00`);
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function monthLabel(date, locale) {
  return date.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

function monthCells(year, month) {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: offset + days }, (_, index) => (
    index < offset ? null : index - offset + 1
  ));
}

function latestInWindow(dates, from, to) {
  return dates.filter((iso) => iso >= from && iso <= to).at(-1) || null;
}

export default function V2SessionDayPicker({
  selectedDate,
  sessionDates = [],
  latestDate,
  onSelectDate,
  loading = false,
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selectedDate);
  const [cursor, setCursor] = useState(monthStart(selectedDate || latestDate || toIsoDate()));
  const rootRef = useRef(null);
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "fr-FR";
  const today = toIsoDate();
  const dates = useMemo(() => [...new Set(sessionDates)].sort(), [sessionDates]);
  const dateSet = useMemo(() => new Set(dates), [dates]);

  useEffect(() => {
    if (!open) return undefined;
    setDraft(selectedDate);
    setCursor(monthStart(selectedDate || latestDate || today));
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [latestDate, open, selectedDate, today]);

  const yesterday = shiftIsoDate(today, -1);
  const last7 = latestInWindow(dates, shiftIsoDate(today, -6), today);
  const last30 = latestInWindow(dates, shiftIsoDate(today, -29), today);
  const shortcuts = [
    { id: "today", date: dateSet.has(today) ? today : null },
    { id: "yesterday", date: dateSet.has(yesterday) ? yesterday : null },
    { id: "last7", date: last7 },
    { id: "last30", date: last30 },
  ];

  const apply = (iso) => {
    if (!iso || !dateSet.has(iso)) return;
    onSelectDate?.(iso);
    setOpen(false);
  };

  const label = !selectedDate
    ? t("v2.overview.days.pick")
    : selectedDate === today
      ? t("v2.overview.days.today")
      : new Date(`${selectedDate}T00:00:00`).toLocaleDateString(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

  const cells = monthCells(cursor.getFullYear(), cursor.getMonth());

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className={`${FILTER_BTN} ${open ? "border-v2-cyan bg-v2-accent" : ""} ${loading ? "border-[rgba(52,203,255,0.45)]" : ""}`}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-busy={loading || undefined}
      >
        <CalendarDays size={15} strokeWidth={1.8} />
        <span>{label}</span>
        {loading ? <V2Spinner size={13} /> : <ChevronDown size={15} />}
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] z-30 w-[540px] rounded-xl border border-v2-border bg-[#19144e] p-3 shadow-v2-pop max-[720px]:w-[min(92vw,420px)]"
          role="dialog"
          aria-label={t("v2.overview.days.calendar")}
        >
          <div className="grid grid-cols-[148px_minmax(0,1fr)] gap-3 max-[720px]:grid-cols-1">
            <div className="flex flex-col gap-0.5 border-r border-[rgba(98,94,166,0.28)] pr-2 max-[720px]:flex-row max-[720px]:flex-wrap max-[720px]:border-b max-[720px]:border-r-0 max-[720px]:pb-2 max-[720px]:pr-0">
              {shortcuts.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  disabled={!item.date}
                  className={`${SHORTCUT} ${draft === item.date ? "bg-[rgba(52,203,255,0.12)] text-[#34cbff]" : "bg-transparent hover:bg-[rgba(98,94,166,0.2)]"}`}
                  onClick={() => {
                    if (!item.date) return;
                    setDraft(item.date);
                    setCursor(monthStart(item.date));
                  }}
                >
                  {t(`v2.overview.days.${item.id}`)}
                </button>
              ))}
              <button
                type="button"
                className={`${SHORTCUT} ${!shortcuts.some((item) => item.date === draft) ? "bg-[rgba(52,203,255,0.12)] text-[#34cbff]" : "bg-transparent hover:bg-[rgba(98,94,166,0.2)]"}`}
                onClick={() => setDraft(selectedDate || latestDate)}
              >
                {t("v2.overview.days.custom")}
              </button>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <button
                  type="button"
                  className="grid h-7 w-7 place-items-center rounded-md border-0 bg-transparent text-[#c9c5f5] hover:bg-[rgba(98,94,166,0.2)]"
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                  aria-label={t("v2.overview.days.prev_month")}
                >
                  <ChevronLeft size={16} />
                </button>
                <strong className="text-[13px] capitalize text-[#f4f2ff]">
                  {monthLabel(cursor, locale)}
                </strong>
                <button
                  type="button"
                  className="grid h-7 w-7 place-items-center rounded-md border-0 bg-transparent text-[#c9c5f5] hover:bg-[rgba(98,94,166,0.2)]"
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                  aria-label={t("v2.overview.days.next_month")}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
              <div className="grid grid-cols-7 text-center text-[10px] font-bold uppercase tracking-[0.06em] text-[#8f8abf]">
                {WEEKDAYS.map((day) => (
                  <span key={day} className="py-1">{t(`v2.overview.trends.dow.${day}`)}</span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-y-1">
                {cells.map((day, index) => {
                  if (!day) return <span key={`empty-${index}`} />;
                  const iso = toIsoDate(new Date(cursor.getFullYear(), cursor.getMonth(), day));
                  const hasSession = dateSet.has(iso);
                  const selected = draft === iso;
                  return (
                    <button
                      key={iso}
                      type="button"
                      disabled={!hasSession}
                      onClick={() => hasSession && setDraft(iso)}
                      className={`mx-auto grid h-8 w-8 place-items-center rounded-full border-0 text-[12px] ${
                        selected
                          ? "bg-[#3b6bff] font-bold text-white"
                          : hasSession
                            ? "bg-[rgba(59,107,255,0.18)] font-semibold text-[#dce7ff] hover:bg-[rgba(59,107,255,0.32)]"
                            : "bg-transparent text-[#5f5a93]"
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-[rgba(98,94,166,0.28)] pt-2.5">
            <button
              type="button"
              className="border-0 bg-transparent px-2 text-[12px] font-semibold text-[#c9c5f5] hover:text-white"
              onClick={() => {
                setDraft(latestDate);
                if (latestDate) setCursor(monthStart(latestDate));
              }}
              disabled={!latestDate}
            >
              {t("v2.overview.days.reset")}
            </button>
            <button
              type="button"
              className="rounded-md bg-[#3b6bff] px-3 py-1.5 text-[12px] font-bold text-white disabled:opacity-40"
              onClick={() => apply(draft)}
              disabled={!draft || !dateSet.has(draft)}
            >
              {t("v2.overview.days.apply")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
